import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analysisApi } from '@/services/analysisApi';
import DemoExplainer from '@/components/DemoExplainer';
import InlineMessage from '@/components/InlineMessage';
import HintBar from '@/components/HintBar';

const HINTS = [
  'Choose how this organisation competes — it shapes the 4B weighting',
  'Cost efficiency → weights Bot and Borrow higher',
  'Quality / Innovation → weights Build and Buy higher',
  'You can change this later in Scenarios',
];

// ── Design tokens (dark theme — values resolve via tokens.css) ────────
// teal/gold kept as hex so `${T.teal}XX` alpha concatenations stay valid.
const T = {
  bg: 'var(--bg)',
  text: 'var(--text)',
  textMuted: 'var(--t3)',
  teal: '#00D4C8',
  gold: '#C9A84C',
  cardBg: 'rgba(255,255,255,0.025)',
  cardBorder: 'rgba(255,255,255,0.07)',
  cardShadow: 'none',
  radius: '12px',
};

const STEPS = ['Upload', 'Discipline', 'Analysis', 'Blueprint'];

const StepIndicator = ({ current = 1 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
    <span style={{ fontSize: 13, color: T.textMuted, fontFamily: 'Inter, sans-serif' }}>
      Step {current + 1} of {STEPS.length}
    </span>
    <div style={{ display: 'flex', gap: 4 }}>
      {STEPS.map((s, i) => (
        <div
          key={s}
          style={{
            width: 32, height: 4, borderRadius: 2,
            background: i <= current ? T.teal : T.cardBorder,
            transition: 'background 0.3s',
          }}
        />
      ))}
    </div>
  </div>
);

// ── Discipline definitions ────────────────────────────

const DISCIPLINES = [
  {
    key: 'operational_excellence',
    label: 'Operational Excellence',
    desc: 'Prioritises automation and fractional reallocation to reduce cost-to-serve.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
  },
  {
    key: 'customer_intimacy',
    label: 'Customer Intimacy',
    desc: 'Frees human talent from admin to focus on relationships and empathetic work.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
      </svg>
    ),
  },
  {
    key: 'product_leadership',
    label: 'Product Leadership',
    desc: 'Removes analytical admin from senior roles to unlock capacity for strategic work.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="9" y1="18" x2="15" y2="18" /><line x1="10" y1="22" x2="14" y2="22" />
        <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 018.91 14" />
      </svg>
    ),
  },
  {
    key: 'growth_capacity',
    label: 'Growth Capacity',
    desc: 'Builds a lean architecture that scales without proportional headcount growth.',
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
      </svg>
    ),
  },
];

// ── Function-specific insight copy ────────────────────

const INSIGHTS = {
  Finance: {
    operational_excellence: 'Identifies Finance tasks that can be automated or fractionalised to reduce cost-to-serve.',
    customer_intimacy: 'Frees Finance talent from admin to focus on business partnering and stakeholder relationships.',
    product_leadership: 'Removes analytical admin from senior Finance roles to unlock capacity for strategic work.',
    growth_capacity: 'Builds a lean Finance architecture that scales without proportional headcount growth.',
  },
  Technology: {
    operational_excellence: 'Automates repetitive engineering tasks like code review triage, dependency updates, and monitoring.',
    customer_intimacy: 'Frees engineers from admin to focus on developer experience and internal stakeholder support.',
    product_leadership: 'Removes operational overhead from senior engineers so they can focus on architecture and R&D.',
    growth_capacity: 'Creates a scalable engineering model using fractional specialists and AI-assisted development.',
  },
  Operations: {
    operational_excellence: 'Targets scheduling, tracking, and reporting tasks for automation to reduce operational overhead.',
    customer_intimacy: 'Frees operations staff from data processing to focus on vendor relationships and change management.',
    product_leadership: 'Removes admin burden from senior operations leaders to unlock process innovation capacity.',
    growth_capacity: 'Builds an operations function that can handle 2x volume without proportional headcount increase.',
  },
  HR: {
    operational_excellence: 'Automates resume screening, scheduling, onboarding docs, and HR reporting to cut admin cost.',
    customer_intimacy: 'Frees HR business partners from transactional work to focus on employee relations and coaching.',
    product_leadership: 'Removes admin from CHRO and HR leads so they can drive organisational design and culture strategy.',
    growth_capacity: 'Creates a scalable people function using fractional comp specialists and AI-powered recruitment.',
  },
  Sales: {
    operational_excellence: 'Automates lead scoring, CRM maintenance, and pipeline reporting to reduce sales admin overhead.',
    customer_intimacy: 'Frees sales reps from data entry to spend more time on client relationship building.',
    product_leadership: 'Removes forecast admin from sales leaders to focus on strategy and key account development.',
    growth_capacity: 'Builds a sales engine that scales territory coverage through AI and fractional enablement.',
  },
  Marketing: {
    operational_excellence: 'Automates content scheduling, campaign reporting, and SEO tasks to reduce marketing admin.',
    customer_intimacy: 'Frees marketers from report compilation to focus on customer insight and creative collaboration.',
    product_leadership: 'Removes production admin from creative directors to unlock brand strategy capacity.',
    growth_capacity: 'Creates a marketing function that can double campaign output without proportional headcount.',
  },
};

const DEFAULT_INSIGHTS = {
  operational_excellence: 'Identifies tasks that can be automated or fractionalised to reduce cost-to-serve.',
  customer_intimacy: 'Frees talent from admin to focus on relationships and empathetic work.',
  product_leadership: 'Removes admin from senior roles to unlock capacity for strategic work.',
  growth_capacity: 'Builds a lean architecture that scales without proportional headcount growth.',
};

// ── Component ─────────────────────────────────────────

const AnalysisDiscipline = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [func, setFunc] = useState('');
  const [roles, setRoles] = useState([]);
  const [inlineMsg, setInlineMsg] = useState(null);

  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  useEffect(() => {
    const f = localStorage.getItem('analysis_function') || '';
    const r = JSON.parse(localStorage.getItem('analysis_roles') || '[]');
    const d = localStorage.getItem('analysis_discipline') || '';
    setFunc(f);
    setRoles(r);
    if (d) setSelected(d);

    const sid = localStorage.getItem('reai_session_id') || localStorage.getItem('analysis_session_id');
    if (!sid) {
      navigate('/analysis');
      return;
    }

    // Also try to restore from session API
    if (sid && !d) {
      analysisApi.getSession(sid).then((res) => {
        const session = res.data?.session;
        if (session?.value_discipline) {
          setSelected(session.value_discipline);
        }
        if (session?.function && !f) setFunc(session.function);
        if (session?.roles?.length && !r.length) setRoles(session.roles);
      }).catch(() => {});
    }
  }, [navigate]);

  const getInsight = (disciplineKey) => {
    const funcInsights = INSIGHTS[func] || DEFAULT_INSIGHTS;
    return funcInsights[disciplineKey] || DEFAULT_INSIGHTS[disciplineKey];
  };

  const handleAnalyse = async () => {
    if (!selected) {
      setInlineMsg({ type: 'warning', title: 'Choose your strategic lens first', message: "Select the value discipline that best matches your organisation's current strategic priority.", suggestions: ["Not sure? Choose Operational Excellence to start. You can re-run with a different lens."] });
      return;
    }
    setInlineMsg(null);
    setLoading(true);
    try {
      const sessionId = localStorage.getItem('reai_session_id') || localStorage.getItem('analysis_session_id');
      const res = await analysisApi.molecularise(sessionId, selected);
      localStorage.setItem('analysis_mol_results', JSON.stringify(res.data));
      localStorage.setItem('analysis_discipline', selected);
      navigate('/analysis/results');
    } catch (err) {
      console.error(err);
      setInlineMsg({
        type: 'error',
        title: 'Analysis took longer than expected',
        message: 'Our AI is analysing your roles. This sometimes takes up to 60 seconds for complex JDs.',
        suggestions: ['Your roles are saved. Come back and click Analyse Roles again.'],
        actions: [{ label: 'Try Again', onClick: () => { setInlineMsg(null); handleAnalyse(); } }],
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: 'var(--fs)', color: T.text, position: 'relative', zIndex: 2 }}>
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '64px 40px 120px' }}>
        {/* Stepper (recipe 3.14) */}
        <div className="stepper" style={{ marginBottom: 16 }}>
          <div className="stepper-top">
            <span className="stepper-label">Step 2 of 4</span>
            <button className="stepper-back" onClick={() => navigate('/analysis')}>← Back</button>
          </div>
          <div className="stepper-rail">
            <div className="stepper-fill" style={{ width: '50%' }} />
          </div>
        </div>

        <HintBar screenId="analysis-discipline" hints={HINTS} />

        <h1 className="h1" style={{ margin: '8px 0 8px', fontSize: 36 }}>
          Choose a value discipline
        </h1>
        <p style={{ fontSize: 15, color: 'var(--t3)', lineHeight: 1.6, marginBottom: 8 }}>
          How this company competes — we'll weight the 4B recommendation around it.
        </p>
        {func && (
          <p className="eyebrow eye-teal" style={{ marginBottom: 40 }}>
            {roles.length} roles in {func}
          </p>
        )}

        {/* Inline messages */}
        {inlineMsg && <InlineMessage {...inlineMsg} onDismiss={() => setInlineMsg(null)} />}

        {/* Discipline grid (recipe 3.14 .disc-grid / .disc-card) */}
        <div className="disc-grid" style={{ marginBottom: 48 }}>
          {DISCIPLINES.map((d) => (
            <div
              key={d.key}
              className={`disc-card${selected === d.key ? ' on' : ''}`}
              onClick={() => setSelected(d.key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelected(d.key)}
            >
              <div className="disc-name">{d.label}</div>
              <div className="disc-desc">{d.desc}</div>
              <div className="disc-desc" style={{ fontSize: 12, color: 'var(--t4)', marginTop: 4 }}>
                {getInsight(d.key)}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div style={{
          paddingTop: 24, borderTop: '1px solid var(--line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <button className="btn-ghost" onClick={() => navigate('/analysis')}>
            Back to roles
          </button>
          <button
            className="btn-teal"
            onClick={handleAnalyse}
            disabled={!selected || loading}
            style={{
              opacity: selected ? (loading ? 0.6 : 1) : 0.4,
              cursor: selected ? (loading ? 'wait' : 'pointer') : 'not-allowed',
              pointerEvents: selected ? 'auto' : 'none',
            }}
          >
            {loading ? 'Analysing your roles…' : 'Analyse Roles →'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AnalysisDisciplineWithExplainer = (props) => (<><AnalysisDiscipline {...props} /><DemoExplainer screen="ANALYSIS_DISCIPLINE" /></>);
export default AnalysisDisciplineWithExplainer;
